import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useLanguage from "../hooks/useLanguage";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import { useTheme } from "@mui/material/styles";
import TopNavBar from "../components/TopNavBar";
import { useAuth } from "../context/AuthContext";
import { addJobNote, getAllJobs, updateJob, updateJobNote } from "../api/jobs";
import ServicesJobList from "../components/servicesPage/ServicesJobList";
import ServicesJobDetailDrawer from "../components/servicesPage/ServicesJobDetailDrawer";
import {
  INTERNAL_ROLES,
  calculateTimeSpentMinutes,
  formatPartsUsedForInput,
  getDefaultTaskStatusFilter,
  getMyServicesFetchWindow,
  getTaskStableKey,
  isCurrentUserJob,
  isTerminalTaskStatus,
  isVisibleAssignmentPeriodStatus,
  normalizeTaskStatus,
  orderJobsByRoute,
  applySavedJobOrder,
  loadJobOrderStore,
  saveJobOrderStore,
  pruneJobOrderStore,
  getJobOrderUserKey,
  dateKeyForDayTab,
  parsePartsUsedInput,
  resolveNextJobStatus,
  shouldPromptFailureReason,
  toDateBucket,
  toText,
  toUtcDate,
  wait,
  AUTO_FAIL_PENDING_TASK_REASON,
} from "../components/servicesPage/servicesUtils";
import fieldServiceTranslations from "../assets/variables/fieldServiceTranslations";

export default function ServicesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useAuth();
  const username =
    user?.username || user?.uk_username || user?.email || "Unknown";
  const currentUserId = String(user?.pk_id ?? user?.id ?? user?.userId ?? "");
  const role = toText(user?.role);
  const isRC = role === "admin" || role === "user";
  const isInternalRole = INTERNAL_ROLES.includes(role);

  // Dev-only escape hatch to preview the technician view without logging in
  // as a different role. Set REACT_APP_ENABLE_SERVICES_VIEW_TOGGLE=true
  // locally; leave unset (or false) in production to hide the toggle.
  const enableViewToggle =
    (process.env.REACT_APP_ENABLE_SERVICES_VIEW_TOGGLE || "").toLowerCase() ===
    "true";
  const [viewModeOverride, setViewModeOverride] = useState(null);
  const viewMode =
    viewModeOverride || (isInternalRole ? "internal" : "technician");

  const [language, setLanguage] = useLanguage();

  const t = (key) => {
    const keys = key.split(".");
    let value = fieldServiceTranslations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [panelError, setPanelError] = useState("");
  const [jobs, setJobs] = useState([]);
  // Manual job orders, stored per user and per calendar date together with
  // the route order they were made against (see servicesUtils). Lazily
  // initialized from localStorage so it survives reloads.
  const [jobOrderStore, setJobOrderStore] = useState(loadJobOrderStore);
  // Shown once when the office changed the route order and a manual order
  // for the day was dropped because of it.
  const [routeOrderNotice, setRouteOrderNotice] = useState(false);
  // jobOrderToggle switches the job list into "reorder mode": tapping a job
  // picks it, tapping a second job swaps the two positions.
  const [jobOrderToggle, setJobOrderToggle] = useState(false);
  const [jobOrderSelectedId, setJobOrderSelectedId] = useState(null);
  const [dayTab, setDayTab] = useState("today");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedTaskKeys, setExpandedTaskKeys] = useState({});
  const [taskDrafts, setTaskDrafts] = useState({});
  const [taskStatusFilter, setTaskStatusFilter] = useState("todo");
  const [animatingTaskKeys, setAnimatingTaskKeys] = useState({});
  const [taskFailureHighlightKeys, setTaskFailureHighlightKeys] = useState({});
  const [pendingTaskStatusByKey, setPendingTaskStatusByKey] = useState({});
  const [pendingFailureFocusTaskKey, setPendingFailureFocusTaskKey] =
    useState(null);
  const [showFailJobInline, setShowFailJobInline] = useState(false);
  const [failJobReasonTouched, setFailJobReasonTouched] = useState(false);
  const [failJobReason, setFailJobReason] = useState("");
  // Live seconds elapsed since job was started — updates every second
  const [jobElapsedSeconds, setJobElapsedSeconds] = useState(0);
  const jobTimerRef = useRef(null);
  const failureInputRefs = useRef({});
  const taskListScrollRef = useRef(null);
  const drawerPullStartYRef = useRef(null);
  const drawerPullDistanceRef = useRef(0);

  const loadFieldData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const jobsResponse = await getAllJobs(getMyServicesFetchWindow());

      const allJobs = Array.isArray(jobsResponse)
        ? jobsResponse
        : Array.isArray(jobsResponse?.jobs)
          ? jobsResponse.jobs
          : Array.isArray(jobsResponse?.data)
            ? jobsResponse.data
            : [];
      console.log(allJobs);

      const myJobs = allJobs.filter(
        (job) =>
          isCurrentUserJob(job, user, role) &&
          isVisibleAssignmentPeriodStatus(job),
      );
      setJobs(myJobs);
    } catch (err) {
      setError(err?.message || "Failed to load your field services");
    } finally {
      setLoading(false);
    }
  }, [role, user]);

  useEffect(() => {
    loadFieldData();
  }, [loadFieldData]);

  // Persist manual job ordering so it survives a page refresh.
  useEffect(() => {
    saveJobOrderStore(jobOrderStore);
  }, [jobOrderStore]);

  // Reset any in-progress swap selection whenever reorder mode is turned
  // off or the visible day tab changes.
  useEffect(() => {
    setJobOrderSelectedId(null);
  }, [jobOrderToggle, dayTab]);

  useEffect(() => {
    if (!drawerOpen || !isMobile) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverscrollY = html.style.overscrollBehaviorY;
    const prevBodyOverscrollY = body.style.overscrollBehaviorY;

    html.style.overscrollBehaviorY = "none";
    body.style.overscrollBehaviorY = "none";

    return () => {
      html.style.overscrollBehaviorY = prevHtmlOverscrollY;
      body.style.overscrollBehaviorY = prevBodyOverscrollY;
    };
  }, [drawerOpen, isMobile]);

  const handleDrawerPullStart = useCallback(
    (event) => {
      if (!isMobile) return;
      const y = event.touches?.[0]?.clientY;
      drawerPullStartYRef.current = typeof y === "number" ? y : null;
      drawerPullDistanceRef.current = 0;
    },
    [isMobile],
  );

  const handleDrawerPullMove = useCallback(
    (event) => {
      if (!isMobile || drawerPullStartYRef.current == null) return;
      const y = event.touches?.[0]?.clientY;
      if (typeof y !== "number") return;

      const delta = y - drawerPullStartYRef.current;
      if (delta > 0) {
        drawerPullDistanceRef.current = delta;
        if (event.cancelable) {
          event.preventDefault();
        }
      }
    },
    [isMobile],
  );

  const handleDrawerPullEnd = useCallback(() => {
    if (!isMobile) {
      drawerPullStartYRef.current = null;
      drawerPullDistanceRef.current = 0;
      return;
    }

    const pulledDistance = drawerPullDistanceRef.current;
    drawerPullStartYRef.current = null;
    drawerPullDistanceRef.current = 0;

    if (pulledDistance > 72) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  const closeDrawerAfterTerminalUpdate = useCallback(() => {
    setSelectedJobId(null);
    setDrawerOpen(false);
  }, []);

  const jobOrderUserKey = getJobOrderUserKey(user);
  const dayTabDateKey = dateKeyForDayTab(dayTab);
  const savedDayOrder = jobOrderStore[jobOrderUserKey]?.[dayTabDateKey] || null;

  const dayOrdering = useMemo(() => {
    const rows = jobs.filter((job) => {
      const sourceDate = job?.scheduled_start || job?.created_at;
      return toDateBucket(sourceDate) === dayTab;
    });

    // Route order: period execution order, master order across unmerged
    // periods of one template, urgent/high priority ahead (servicesUtils).
    const routeOrdered = orderJobsByRoute(rows);
    const routeOrderIds = routeOrdered.map((job) => String(job.pk_id));

    // Completed jobs always sink to the bottom of the list, even if a
    // manual order would otherwise place them elsewhere. Relative order
    // within each group (active / completed) is preserved.
    const sinkCompleted = (list) => {
      const active = [];
      const completed = [];
      list.forEach((job) => {
        if (toText(job?.status) === "completed") {
          completed.push(job);
        } else {
          active.push(job);
        }
      });
      return [...active, ...completed];
    };

    // The user's manual order for this date wins, unless the route order
    // changed since it was made (then it is stale and dropped below).
    const { jobs: ordered, stale } = applySavedJobOrder(routeOrdered, savedDayOrder);

    return { jobs: sinkCompleted(ordered), routeOrderIds, stale };
  }, [dayTab, jobs, savedDayOrder]);

  const dayFilteredJobs = dayOrdering.jobs;

  // Drop a manual order that no longer matches the route and tell the user.
  useEffect(() => {
    if (!dayOrdering.stale) return;
    setJobOrderStore((prev) => {
      const userDays = { ...(prev[jobOrderUserKey] || {}) };
      delete userDays[dayTabDateKey];
      return pruneJobOrderStore({ ...prev, [jobOrderUserKey]: userDays });
    });
    setRouteOrderNotice(true);
  }, [dayOrdering.stale, jobOrderUserKey, dayTabDateKey]);

  const resetToRouteOrder = useCallback(() => {
    setJobOrderStore((prev) => {
      const userDays = { ...(prev[jobOrderUserKey] || {}) };
      delete userDays[dayTabDateKey];
      return pruneJobOrderStore({ ...prev, [jobOrderUserKey]: userDays });
    });
    setJobOrderToggle(false);
  }, [jobOrderUserKey, dayTabDateKey]);

  // Swap two jobs' positions within the current day tab and persist the
  // resulting order. The first tap in reorder mode picks a job; the second
  // tap on a different job swaps it with the first, then clears selection.
  const handleJobOrderSelect = useCallback(
    (jobId) => {
      const key = String(jobId);
      // Completed jobs always sink to the bottom of the list, so picking
      // one to reorder would be a no-op that immediately snaps back.
      const tappedJob = dayFilteredJobs.find(
        (job) => String(job.pk_id) === key,
      );
      if (tappedJob && toText(tappedJob.status) === "completed") {
        return;
      }

      if (!jobOrderSelectedId) {
        setJobOrderSelectedId(key);
        return;
      }

      if (jobOrderSelectedId === key) {
        setJobOrderSelectedId(null);
        return;
      }

      setJobOrderStore((prevStore) => {
        // Start from what is on screen (route order + any manual order).
        const order = dayFilteredJobs.map((job) => String(job.pk_id));

        const indexA = order.indexOf(jobOrderSelectedId);
        const indexB = order.indexOf(key);
        if (indexA === -1 || indexB === -1) return prevStore;

        [order[indexA], order[indexB]] = [order[indexB], order[indexA]];

        return pruneJobOrderStore({
          ...prevStore,
          [jobOrderUserKey]: {
            ...(prevStore[jobOrderUserKey] || {}),
            // baseline = the route order this manual order was made against.
            [dayTabDateKey]: { order, baseline: dayOrdering.routeOrderIds },
          },
        });
      });

      setJobOrderSelectedId(null);
    },
    [dayFilteredJobs, dayOrdering.routeOrderIds, jobOrderSelectedId, jobOrderUserKey, dayTabDateKey],
  );
  const handleDayTabChange = useCallback((_, value) => {
    // Reorder mode (and any in-progress pick) is scoped to a single day
    // tab. Leaving it active across tabs would let a job picked on one
    // day get swapped with a job on another, which doesn't make sense.
    setJobOrderToggle(false);
    setJobOrderSelectedId(null);
    setDayTab(value);
  }, []);

  const dayJobCounts = useMemo(() => {
    const counts = {
      yesterday: { total: 0, finished: 0 },
      today: { total: 0, finished: 0 },
      tomorrow: { total: 0, finished: 0 },
    };

    jobs.forEach((job) => {
      const sourceDate = job?.scheduled_start || job?.created_at;
      const bucket = toDateBucket(sourceDate);
      if (bucket in counts) {
        counts[bucket].total += 1;
        if (
          ["completed", "failed", "cancelled"].includes(toText(job?.status))
        ) {
          counts[bucket].finished += 1;
        }
      }
    });

    return counts;
  }, [jobs]);

  const selectedJob = useMemo(
    () =>
      jobs.find((job) => String(job.pk_id) === String(selectedJobId)) || null,
    [jobs, selectedJobId],
  );

  const selectedTasks = useMemo(() => {
    if (!selectedJob || !Array.isArray(selectedJob.tasks)) return [];
    return selectedJob.tasks;
  }, [selectedJob]);

  const selectedTaskProgress = useMemo(() => {
    const total = selectedTasks.length;
    if (total === 0) return { total: 0, done: 0 };

    const done = selectedTasks.filter((task) =>
      isTerminalTaskStatus(task?.status),
    ).length;

    return { total, done };
  }, [selectedTasks]);

  const selectedTaskBreakdown = useMemo(() => {
    const total = selectedTasks.length;
    const completed = selectedTasks.filter(
      (task) => normalizeTaskStatus(task?.status) === "completed",
    ).length;
    const failed = selectedTasks.filter(
      (task) => normalizeTaskStatus(task?.status) === "failed",
    ).length;
    const skippedOrNa = selectedTasks.filter((task) => {
      const status = normalizeTaskStatus(task?.status);
      return status === "skipped" || status === "not_applicable";
    }).length;

    const done = completed + failed + skippedOrNa;
    const pending = Math.max(0, total - done);

    return {
      total,
      completed,
      failed,
      skippedOrNa,
      pending,
      completedPct: total > 0 ? (completed / total) * 100 : 0,
      failedPct: total > 0 ? (failed / total) * 100 : 0,
      skippedOrNaPct: total > 0 ? (skippedOrNa / total) * 100 : 0,
    };
  }, [selectedTasks]);

  const selectedJobTotalMinutes = useMemo(() => {
    if (!selectedJob) return null;

    const startedAt = toUtcDate(selectedJob.actual_start);
    const endedAt = toUtcDate(selectedJob.actual_end);

    if (startedAt && endedAt) {
      const diffMs = endedAt.getTime() - startedAt.getTime();
      return Math.max(0, Math.round(diffMs / 60000));
    }

    if (
      startedAt &&
      ["in_progress", "in progress"].includes(toText(selectedJob.status))
    ) {
      return Math.max(0, Math.floor(jobElapsedSeconds / 60));
    }

    const summedTaskMinutes = selectedTasks.reduce((sum, task) => {
      const value = Number(task?.time_spent_minutes);
      return Number.isFinite(value) && value >= 0 ? sum + value : sum;
    }, 0);

    if (summedTaskMinutes > 0) return summedTaskMinutes;
    if (selectedTaskProgress.done > 0) return 0;
    return null;
  }, [
    jobElapsedSeconds,
    selectedJob,
    selectedTaskProgress.done,
    selectedTasks,
  ]);

  // Start/stop the job elapsed timer whenever the selected job or its status changes
  useEffect(() => {
    if (jobTimerRef.current) {
      clearInterval(jobTimerRef.current);
      jobTimerRef.current = null;
    }

    const jobStatus = toText(selectedJob?.status);
    const jobStartedAt = toUtcDate(selectedJob?.actual_start);

    if (jobStatus === "in_progress" && jobStartedAt) {
      const tick = () => {
        const diffMs = Date.now() - jobStartedAt.getTime();
        setJobElapsedSeconds(Math.max(0, Math.floor(diffMs / 1000)));
      };
      tick();
      jobTimerRef.current = setInterval(tick, 1000);
    } else {
      setJobElapsedSeconds(0);
    }

    return () => {
      if (jobTimerRef.current) {
        clearInterval(jobTimerRef.current);
        jobTimerRef.current = null;
      }
    };
  }, [selectedJob]);

  useEffect(() => {
    if (!selectedJob) {
      setTaskStatusFilter("todo");
      return;
    }

    setTaskStatusFilter(getDefaultTaskStatusFilter(selectedJob.status));
  }, [selectedJobId, selectedJob?.status]);

  useEffect(() => {
    if (!selectedJob || !Array.isArray(selectedJob.tasks)) {
      setTaskDrafts({});
      setExpandedTaskKeys({});
      setTaskFailureHighlightKeys({});
      setPendingFailureFocusTaskKey(null);
      setPanelError("");
      return;
    }

    const nextDrafts = {};
    selectedJob.tasks.forEach((task, index) => {
      const key = getTaskStableKey(task, index);
      nextDrafts[key] = {
        technician_notes: task?.technician_notes || "",
        failure_reason: task?.failure_reason || "",
        parts_used: formatPartsUsedForInput(task?.parts_used),
      };
    });

    setTaskDrafts(nextDrafts);
    setExpandedTaskKeys({});
    setTaskFailureHighlightKeys({});
    setPendingTaskStatusByKey({});
    setPendingFailureFocusTaskKey(null);
    setShowFailJobInline(false);
    setFailJobReason("");
    setFailJobReasonTouched(false);
    setPanelError("");
  }, [selectedJobId, selectedJob]);

  useEffect(() => {
    if (!pendingFailureFocusTaskKey) return;

    const focusAndScrollToInput = (input) => {
      if (!input || typeof input.focus !== "function") return false;

      input.focus({ preventScroll: true });

      const scrollHost = taskListScrollRef.current;
      if (
        scrollHost &&
        typeof scrollHost.scrollTo === "function" &&
        typeof input.getBoundingClientRect === "function"
      ) {
        const hostRect = scrollHost.getBoundingClientRect();
        const inputRect = input.getBoundingClientRect();
        const topWithinHost =
          inputRect.top - hostRect.top + scrollHost.scrollTop;
        const targetTop = Math.max(0, topWithinHost - hostRect.height * 0.28);
        scrollHost.scrollTo({ top: targetTop, behavior: "smooth" });
      } else if (typeof input.scrollIntoView === "function") {
        input.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }

      return true;
    };

    const targetInput = failureInputRefs.current[pendingFailureFocusTaskKey];
    if (focusAndScrollToInput(targetInput)) {
      setPendingFailureFocusTaskKey(null);
      return;
    }

    const retryId = window.setTimeout(() => {
      const retryInput = failureInputRefs.current[pendingFailureFocusTaskKey];
      if (focusAndScrollToInput(retryInput)) {
        setPendingFailureFocusTaskKey(null);
      }
    }, 60);

    return () => window.clearTimeout(retryId);
  }, [pendingFailureFocusTaskKey, expandedTaskKeys]);

  const patchJobInState = useCallback((jobPkId, updates, updatedJob = null) => {
    setJobs((prevJobs) =>
      prevJobs.map((row) => {
        if (String(row.pk_id) !== String(jobPkId)) {
          return row;
        }

        const tasks = Array.isArray(updatedJob?.tasks)
          ? updatedJob.tasks
          : Array.isArray(updates.tasks)
            ? updates.tasks
            : Array.isArray(row.tasks)
              ? row.tasks
              : [];

        return {
          ...row,
          ...updates,
          ...(updatedJob || {}),
          tasks,
          total_job_tasks: updatedJob?.total_job_tasks ?? tasks.length,
          updated_at: updatedJob?.updated_at || new Date().toISOString(),
        };
      }),
    );
  }, []);

  const noteEntriesField = (noteType) =>
    noteType === "internal" ? "internal_notes_entries" : "job_notes_entries";

  const handleAddNote = useCallback(
    async (noteType, content) => {
      if (!selectedJob) return;
      const field = noteEntriesField(noteType);
      try {
        const response = await addJobNote(selectedJob.pk_id, {
          noteType,
          content,
        });
        patchJobInState(selectedJob.pk_id, {
          [field]: response.entries || [],
        });
      } catch (err) {
        setPanelError(err?.message || "Failed to add note");
      }
    },
    [patchJobInState, selectedJob],
  );

  const handleUpdateNote = useCallback(
    async (noteType, entryId, content) => {
      if (!selectedJob) return;
      const field = noteEntriesField(noteType);
      try {
        const response = await updateJobNote(selectedJob.pk_id, entryId, {
          noteType,
          content,
        });
        patchJobInState(selectedJob.pk_id, {
          [field]: response.entries || [],
        });
      } catch (err) {
        setPanelError(err?.message || "Failed to update note");
      }
    },
    [patchJobInState, selectedJob],
  );

  const handleStartJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    const startedAt = selectedJob.actual_start || new Date().toISOString();

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, {
        status: "in_progress",
        actual_start: startedAt,
        actual_end: null,
      });
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(
        selectedJob.pk_id,
        {
          status: "in_progress",
          actual_start: startedAt,
          actual_end: null,
        },
        updatedJob,
      );
    } catch (err) {
      setPanelError(err?.message || "Failed to start job");
    } finally {
      setSaving(false);
    }
  }, [patchJobInState, saving, selectedJob]);

  const handleCompleteJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, {
        status: "completed",
      });
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(selectedJob.pk_id, { status: "completed" }, updatedJob);
      closeDrawerAfterTerminalUpdate();
    } catch (err) {
      setPanelError(err?.message || "Failed to complete job");
    } finally {
      setSaving(false);
    }
  }, [closeDrawerAfterTerminalUpdate, patchJobInState, saving, selectedJob]);

  // Marks every still-pending task as completed (preserving any notes/parts
  // already entered on them) and completes the job in one action, for the
  // "Complete job" button at the bottom of the task list.
  const handleCompleteAllTasksAndJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    const currentTasks = Array.isArray(selectedJob.tasks)
      ? selectedJob.tasks
      : [];

    const nowIso = new Date().toISOString();
    const jobActualStart = selectedJob.actual_start || nowIso;

    // Chain each newly-completed task's start time off the latest already
    // finished task, same as the single-task completion flow, so times
    // stay sequential instead of all reading zero minutes.
    let chainStartedAt =
      currentTasks
        .filter(
          (task) => isTerminalTaskStatus(task?.status) && task?.completed_at,
        )
        .map((task) => task.completed_at)
        .sort()
        .at(-1) || jobActualStart;

    const updatedTasks = currentTasks.map((task) => {
      if (isTerminalTaskStatus(task?.status)) {
        // Already finished — leave its status, notes, and timing untouched.
        return task;
      }

      const startedAt = task.started_at || chainStartedAt;
      const completedAt = nowIso;
      chainStartedAt = completedAt;

      return {
        ...task,
        status: "completed",
        started_at: startedAt,
        completed_at: completedAt,
        time_spent_minutes: calculateTimeSpentMinutes(startedAt, completedAt),
      };
    });

    const updates = {
      tasks: updatedTasks,
      status: "completed",
      total_job_tasks: updatedTasks.length,
      actual_start: selectedJob.actual_start || jobActualStart,
      actual_end: nowIso,
    };

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, updates);
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(selectedJob.pk_id, updates, updatedJob);
      closeDrawerAfterTerminalUpdate();
    } catch (err) {
      setPanelError(err?.message || "Failed to complete job");
    } finally {
      setSaving(false);
    }
  }, [closeDrawerAfterTerminalUpdate, patchJobInState, saving, selectedJob]);

  const handleReopenJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    // Send tasks back to pending and clear their timing, but leave every
    // note/finding a technician entered (technician_notes, failure_reason,
    // parts_used, etc.) untouched.
    const nextTasks = (
      Array.isArray(selectedJob.tasks) ? selectedJob.tasks : []
    ).map((task) => ({
      ...task,
      status: "pending",
      started_at: null,
      completed_at: null,
      time_spent_minutes: null,
    }));

    const updates = {
      status: "assigned",
      actual_start: null,
      actual_end: null,
      tasks: nextTasks,
    };

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, updates);
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(selectedJob.pk_id, updates, updatedJob);
    } catch (err) {
      setPanelError(err?.message || "Failed to reopen job");
    } finally {
      setSaving(false);
    }
  }, [patchJobInState, saving, selectedJob]);

  const handleToggleFailJobInline = useCallback(() => {
    if (!selectedJob || saving) return;
    setShowFailJobInline((prev) => {
      if (!prev) {
        setFailJobReason(selectedJob.cancellation_reason || "");
        setFailJobReasonTouched(false);
      }
      return !prev;
    });
  }, [saving, selectedJob]);

  const handleFailJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    const reason = String(failJobReason || "").trim();
    if (!reason) {
      setFailJobReasonTouched(true);
      return;
    }

    const nowIso = new Date().toISOString();
    const nextTasks = (
      Array.isArray(selectedJob.tasks) ? selectedJob.tasks : []
    ).map((task) => {
      if (isTerminalTaskStatus(task?.status)) {
        return task;
      }

      const startedAt = task?.started_at || selectedJob.actual_start || nowIso;
      const completedAt = task?.completed_at || nowIso;

      return {
        ...task,
        status: "failed",
        failure_reason: AUTO_FAIL_PENDING_TASK_REASON,
        started_at: startedAt,
        completed_at: completedAt,
        time_spent_minutes: calculateTimeSpentMinutes(startedAt, completedAt),
      };
    });

    const updates = {
      status: "failed",
      cancellation_reason: reason,
      actual_start: selectedJob.actual_start || nowIso,
      actual_end: nowIso,
      tasks: nextTasks,
    };

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, updates);
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(selectedJob.pk_id, updates, updatedJob);
      closeDrawerAfterTerminalUpdate();
      setShowFailJobInline(false);
      setFailJobReason("");
      setFailJobReasonTouched(false);
    } catch (err) {
      setPanelError(err?.message || "Failed to fail job");
    } finally {
      setSaving(false);
    }
  }, [
    closeDrawerAfterTerminalUpdate,
    failJobReason,
    patchJobInState,
    saving,
    selectedJob,
  ]);

  const toggleTaskExpanded = useCallback((task, index) => {
    const key = getTaskStableKey(task, index);
    setExpandedTaskKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const updateTaskDraft = useCallback((task, index, field, value) => {
    const key = getTaskStableKey(task, index);
    setTaskDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));

    if (field === "failure_reason" && String(value || "").trim()) {
      setTaskFailureHighlightKeys((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, []);

  const handleTaskStatusChange = useCallback(
    async (taskIndex, nextStatus = null) => {
      if (!selectedJob || saving) return;

      const currentTasks = Array.isArray(selectedJob.tasks)
        ? selectedJob.tasks
        : [];
      const targetTask = currentTasks[taskIndex];
      if (!targetTask) return;
      const targetTaskKey = getTaskStableKey(targetTask, taskIndex);
      const targetDraft = taskDrafts[targetTaskKey] || {
        technician_notes: targetTask?.technician_notes || "",
        failure_reason: targetTask?.failure_reason || "",
      };
      const targetPendingStatus = pendingTaskStatusByKey[targetTaskKey] || null;
      const shouldInferFailedFromSaveNotes =
        !nextStatus &&
        !targetPendingStatus &&
        !isTerminalTaskStatus(targetTask?.status) &&
        Boolean(String(targetDraft.failure_reason || "").trim());
      const targetResolvedStatus =
        nextStatus ||
        targetPendingStatus ||
        (shouldInferFailedFromSaveNotes ? "failed" : null) ||
        normalizeTaskStatus(targetTask?.status);
      const missingFailureNote =
        shouldPromptFailureReason(targetResolvedStatus) &&
        !String(targetDraft.failure_reason || "").trim();

      if (missingFailureNote) {
        setExpandedTaskKeys((prev) => ({
          ...prev,
          [targetTaskKey]: true,
        }));
        setTaskFailureHighlightKeys((prev) => ({
          ...prev,
          [targetTaskKey]: true,
        }));
        setPendingTaskStatusByKey((prev) => ({
          ...prev,
          [targetTaskKey]: targetResolvedStatus,
        }));
        setPendingFailureFocusTaskKey(targetTaskKey);
        return;
      }

      const shouldAnimateCompletion =
        nextStatus &&
        isTerminalTaskStatus(targetResolvedStatus) &&
        !isTerminalTaskStatus(targetTask?.status);

      if (shouldAnimateCompletion) {
        setAnimatingTaskKeys((prev) => ({
          ...prev,
          [targetTaskKey]: true,
        }));
        await wait(1000);
      }

      const nowIso = new Date().toISOString();
      const jobActualStart = selectedJob.actual_start || nowIso;

      // Use the most recent completed_at from any already-finished task as this
      // task's started_at so back-to-back tasks share a seamless timeline.
      const lastCompletedAt =
        currentTasks
          .filter(
            (t, i) =>
              i !== taskIndex &&
              isTerminalTaskStatus(t?.status) &&
              t?.completed_at,
          )
          .map((t) => t.completed_at)
          .sort()
          .at(-1) || null;

      const updatedTasks = currentTasks.map((task, index) => {
        if (index !== taskIndex) return task;

        const draftKey = getTaskStableKey(task, index);
        const draft = taskDrafts[draftKey] || {};
        const draftPendingStatus = pendingTaskStatusByKey[draftKey] || null;
        const inferFailedFromSaveNotes =
          !nextStatus &&
          !draftPendingStatus &&
          !isTerminalTaskStatus(task.status) &&
          Boolean(String(draft.failure_reason || "").trim());
        const resolvedStatus =
          nextStatus ||
          draftPendingStatus ||
          (inferFailedFromSaveNotes ? "failed" : null) ||
          normalizeTaskStatus(task.status);
        const persistPartsUsed = ["in_progress", "completed"].includes(
          resolvedStatus,
        );

        let startedAt = task.started_at || null;
        let completedAt = task.completed_at || null;

        if (
          ["completed", "failed", "skipped", "not_applicable"].includes(
            resolvedStatus,
          )
        ) {
          // First finished task starts at the job actual_start. Each later
          // finished task starts at the latest completed task timestamp.
          startedAt = startedAt || lastCompletedAt || jobActualStart;
          // Only stamp completed_at the first time a task reaches a terminal
          // state.  Subsequent note-saves must NOT overwrite it.
          completedAt = completedAt || nowIso;
        }

        const timeSpentMinutes = calculateTimeSpentMinutes(
          startedAt,
          completedAt,
        );

        return {
          ...task,
          status: resolvedStatus,
          started_at: startedAt,
          completed_at: completedAt,
          technician_notes: draft.technician_notes || null,
          failure_reason:
            shouldPromptFailureReason(resolvedStatus) && draft.failure_reason
              ? draft.failure_reason
              : shouldPromptFailureReason(resolvedStatus)
                ? task.failure_reason || null
                : null,
          parts_used: persistPartsUsed
            ? parsePartsUsedInput(draft.parts_used)
            : null,
          time_spent_minutes: timeSpentMinutes,
        };
      });

      const allDone =
        updatedTasks.length > 0 &&
        updatedTasks.every((task) => isTerminalTaskStatus(task?.status));

      // When the first task is resolved the job is automatically in_progress.
      // When all tasks are done the job auto-completes. Terminal job states
      // (completed/failed/etc.) must be preserved when users edit notes.
      const nextJobStatus = resolveNextJobStatus(selectedJob.status, allDone);

      const updates = {
        tasks: updatedTasks,
        status: nextJobStatus,
        total_job_tasks: updatedTasks.length,
        actual_start:
          nextJobStatus === "in_progress" || nextJobStatus === "completed"
            ? selectedJob.actual_start || jobActualStart
            : selectedJob.actual_start || null,
        actual_end: allDone ? nowIso : selectedJob.actual_end || null,
      };

      setSaving(true);
      setPanelError("");
      try {
        const response = await updateJob(selectedJob.pk_id, updates);
        const updatedJob = response?.job || response?.data || response || null;
        patchJobInState(selectedJob.pk_id, updates, updatedJob);
        if (["completed", "failed"].includes(nextJobStatus)) {
          closeDrawerAfterTerminalUpdate();
        }
        setPendingTaskStatusByKey((prev) => {
          if (!prev[targetTaskKey]) return prev;
          const next = { ...prev };
          delete next[targetTaskKey];
          return next;
        });

        if (nextStatus && isTerminalTaskStatus(nextStatus)) {
          setExpandedTaskKeys((prev) => ({
            ...prev,
            [targetTaskKey]: true,
          }));
        }
      } catch (err) {
        setAnimatingTaskKeys((prev) => {
          const next = { ...prev };
          delete next[targetTaskKey];
          return next;
        });
        setPanelError(err?.message || "Failed to update task status");
      } finally {
        setAnimatingTaskKeys((prev) => {
          const next = { ...prev };
          delete next[targetTaskKey];
          return next;
        });
        setSaving(false);
      }
    },
    [
      closeDrawerAfterTerminalUpdate,
      patchJobInState,
      pendingTaskStatusByKey,
      saving,
      selectedJob,
      taskDrafts,
    ],
  );

  return (
    <>
      <TopNavBar
        title={t("pageTitle")}
        username={username}
        heading={t("heading")}
      />

      <Container sx={{ mt: 1.5, mb: 3, px: { xs: 1.25, sm: 2 } }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            mb: 1.5,
          }}
        >
          <CardContent sx={{ py: 1.5 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: "1.45rem" }}
              >
                {t("heading")}
              </Typography>
            </Stack>
            <Box sx={{ flexDirection: "row", display: "flex", gap: 2 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: 0.6, fontSize: "1rem" }}
              >
                {t("currentUser")}: {username}
              </Typography>

              {isRC && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 0.6, fontSize: "1rem" }}
                >
                  {t("profile")}: {role.charAt(0).toUpperCase() + role.slice(1)}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        <Tabs
          value={dayTab}
          onChange={handleDayTabChange}
          variant="fullWidth"
          sx={{ mb: 1.25, minHeight: 50 }}
        >
          <Tab
            value="yesterday"
            label={
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.2, sm: 0.75 }}
                alignItems="center"
              >
                <span>{t("tabs.yesterday")}</span>
                <Chip
                  size="medium"
                  label={`${dayJobCounts.yesterday.finished}/${dayJobCounts.yesterday.total}`}
                  variant="outlined"
                  sx={{ height: 24, fontSize: "0.8rem", px: 0.4 }}
                />
              </Stack>
            }
            sx={{ minHeight: 50, py: 0.9, fontSize: "1rem" }}
          />
          <Tab
            value="today"
            label={
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.2, sm: 0.75 }}
                alignItems="center"
              >
                <span>{t("tabs.today")}</span>
                <Chip
                  size="medium"
                  label={`${dayJobCounts.today.finished}/${dayJobCounts.today.total}`}
                  variant="outlined"
                  sx={{ height: 24, fontSize: "0.8rem", px: 0.4 }}
                />
              </Stack>
            }
            sx={{ minHeight: 50, py: 0.9, fontSize: "1rem" }}
          />
          <Tab
            value="tomorrow"
            label={
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.2, sm: 0.75 }}
                alignItems="center"
              >
                <span>{t("tabs.tomorrow")}</span>
                <Chip
                  size="medium"
                  label={`${dayJobCounts.tomorrow.finished}/${dayJobCounts.tomorrow.total}`}
                  variant="outlined"
                  sx={{ height: 24, fontSize: "0.8rem", px: 0.4 }}
                />
              </Stack>
            }
            sx={{ minHeight: 50, py: 0.9, fontSize: "1rem" }}
          />
        </Tabs>

        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{ mb: 1 }}
        >
          {enableViewToggle && isInternalRole ? (
            <Button
              size="small"
              variant="text"
              onClick={() =>
                setViewModeOverride((prev) =>
                  prev === "technician" ? null : "technician",
                )
              }
            >
              {viewMode === "internal"
                ? "Preview technician view"
                : "Back to internal view"}
            </Button>
          ) : null}
          {jobOrderToggle ? (
            <Typography variant="body2" color="text.secondary">
              {jobOrderSelectedId
                ? t("jobOrdering.jobOrderPickSecond")
                : t("jobOrdering.jobOrderPickFirst")}
            </Typography>
          ) : null}
          {savedDayOrder ? (
            <Button size="small" variant="text" onClick={resetToRouteOrder}>
              {t("jobOrdering.resetToRouteOrder")}
            </Button>
          ) : null}
          <Button
            size="small"
            variant={jobOrderToggle ? "contained" : "outlined"}
            startIcon={<SwapVertRoundedIcon />}
            onClick={() => setJobOrderToggle((prev) => !prev)}
          >
            {jobOrderToggle
              ? t("jobOrdering.doneReording")
              : t("jobOrdering.reorderJobs")}
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        ) : null}

        {routeOrderNotice ? (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            onClose={() => setRouteOrderNotice(false)}
          >
            {t("jobOrdering.routeOrderChanged")}
          </Alert>
        ) : null}

        {!loading && !error ? (
          <ServicesJobList
            jobs={dayFilteredJobs}
            jobOrderToggle={jobOrderToggle}
            jobOrderSelectedId={jobOrderSelectedId}
            onJobOrderSelect={handleJobOrderSelect}
            onOpenJob={(jobId) => {
              setSelectedJobId(jobId);
              setPanelError("");
              setDrawerOpen(true);
            }}
            viewMode={viewMode}
            t={t}
          />
        ) : null}

        <ServicesJobDetailDrawer
          isMobile={isMobile}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onDrawerPullStart={handleDrawerPullStart}
          onDrawerPullMove={handleDrawerPullMove}
          onDrawerPullEnd={handleDrawerPullEnd}
          selectedJob={selectedJob}
          panelError={panelError}
          saving={saving}
          jobElapsedSeconds={jobElapsedSeconds}
          selectedTaskProgress={selectedTaskProgress}
          selectedTaskBreakdown={selectedTaskBreakdown}
          selectedJobTotalMinutes={selectedJobTotalMinutes}
          taskStatusFilter={taskStatusFilter}
          onTaskStatusFilterChange={setTaskStatusFilter}
          selectedTasks={selectedTasks}
          expandedTaskKeys={expandedTaskKeys}
          taskDrafts={taskDrafts}
          animatingTaskKeys={animatingTaskKeys}
          taskFailureHighlightKeys={taskFailureHighlightKeys}
          failureInputRefs={failureInputRefs}
          language={language}
          viewMode={viewMode}
          t={t}
          onStartJob={handleStartJob}
          onCompleteAllTasksAndJob={handleCompleteAllTasksAndJob}
          onReopenJob={handleReopenJob}
          showFailJobInline={showFailJobInline}
          onToggleFailJobInline={handleToggleFailJobInline}
          onFailJob={handleFailJob}
          failJobReason={failJobReason}
          onFailJobReasonChange={(value) => {
            setFailJobReason(value);
            setFailJobReasonTouched(true);
          }}
          failJobReasonTouched={failJobReasonTouched}
          onToggleTaskExpanded={toggleTaskExpanded}
          onUpdateTaskDraft={updateTaskDraft}
          onTaskStatusChange={handleTaskStatusChange}
          onAddNote={handleAddNote}
          onUpdateNote={handleUpdateNote}
          currentUserId={currentUserId}
        />
      </Container>
    </>
  );
}
